import React from "react";
import AthleteHeadshotImage, { usesCompactHeadshot } from "./AthleteHeadshotImage";
import { isFallbackHeadshotImage } from "./athleteHeadshotPlaceholder";
import "../../site/scss/_athlete_board_skeleton.scss";
import {
  ArrowPathIcon,
  ArrowRightIcon,
  ChartBarIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  PlayCircleIcon,
  SelectionDirectionIcon,
  SportIcon,
  ThemeModeIcon,
} from "../icons/heroIcons";

const handleSkeletonSupportClick = (event) => {
  event.preventDefault();

  if (typeof window === "undefined") {
    return;
  }

  if (typeof window.wwOpenTawkChat === "function") {
    window.wwIsTawkChatOpen = true;
    document.body.classList.add("ww-tawk-chat-open");
    window.wwOpenTawkChat();
  } else if (window.Tawk_API && typeof window.Tawk_API.maximize === "function") {
    window.wwIsTawkChatOpen = true;
    document.body.classList.add("ww-tawk-chat-open");
    window.Tawk_API.maximize();
  } else {
    return;
  }

  if (typeof window.wwSyncTawkWidgetOffset === "function") {
    window.wwSyncTawkWidgetOffset();
    window.requestAnimationFrame(window.wwSyncTawkWidgetOffset);
    window.setTimeout(window.wwSyncTawkWidgetOffset, 180);
  }

  window.setTimeout(() => {
    const hasTawkTarget = Array.from(
      document.querySelectorAll(
        'iframe[src*="tawk"], iframe[title*="chat"], iframe[title*="Chat"], iframe[title*="widget"], iframe[title*="Widget"], [id*="tawk"], [id*="Tawk"], [class*="tawk"], [class*="Tawk"]'
      )
    ).some((target) => target !== document.body && target !== document.documentElement);

    if (window.wwIsTawkChatOpen === true && !hasTawkTarget) {
      window.wwIsTawkChatOpen = false;
      document.body.classList.remove("ww-tawk-chat-open");
    }
  }, 2500);
};

const DEFAULT_SPORT_ITEMS = [
  { sport: "topplays", label: "TOP PICKS", selected: true },
];

const SKELETON_SPORT_LABELS = {
  allstarmlb: "MLB ALL-STAR",
  hrderby: "HOME RUN DERBY",
  topplays: "TOP PICKS",
  wnba1h: "WNBA 1H",
  worldcup: "WORLD CUP",
  worldcup1h: "WORLD CUP 1H",
  worldcup2h: "WORLD CUP 2H",
};

const getSkeletonSportItems = (sports, selectedSport) => {
  const normalizedSelectedSport = String(selectedSport || "").trim().toLowerCase();
  const normalizedSports = Array.isArray(sports)
    ? [...new Set(sports.map((sport) => String(sport || "").trim().toLowerCase()).filter(Boolean))]
    : [];

  if (normalizedSelectedSport && !normalizedSports.includes(normalizedSelectedSport)) {
    normalizedSports.unshift(normalizedSelectedSport);
  }

  if (normalizedSports.length === 0) {
    return DEFAULT_SPORT_ITEMS;
  }

  return normalizedSports.map((sport, index) => ({
    sport,
    label: SKELETON_SPORT_LABELS[sport] || sport.toUpperCase(),
    selected: normalizedSelectedSport ? sport === normalizedSelectedSport : index === 0,
  }));
};

const handleSkeletonSportSelection = (sport) => {
  if (typeof window === "undefined" || !sport) {
    return;
  }

  const url = new URL(window.location.href);
  url.searchParams.set("sport", sport);
  window.location.assign(url.toString());
};

const TOP_PLAYS_STAT_ITEMS = [
  { label: "🔥 Popular", selected: true },
  { label: "Teams" },
  { label: "Stat" },
  { label: "Stat" },
  { label: "Stat" },
];

const GAME_ITEMS = Array.from({ length: 10 }, () => ({
  selected: false,
}));

const UTILITY_ITEMS = [
  { label: "Rules", Icon: PlayCircleIcon, className: "mr-2" },
  { label: "Icons", Icon: InformationCircleIcon, className: "ml-2 mr-2" },
  {
    label: "Support",
    Icon: InformationCircleIcon,
    className: "ml-2 mr-2 d-none d-md-inline-block",
    linkHref: "#",
    onClick: handleSkeletonSupportClick,
  },
  { label: "Light Mode", Icon: ThemeModeIcon, className: "ml-2" },
];

const STAT_ROW_STYLE = {
  overflowX: "auto",
  whiteSpace: "nowrap",
  WebkitOverflowScrolling: "touch",
  msOverflowStyle: "none",
  scrollbarWidth: "none",
  marginLeft: 0,
  marginRight: 0,
  cursor: "grab",
  textAlign: "left",
};

const DEFAULT_CARD_COUNTS = {
  app: 5,
  cards: 6,
};

const TOP_PLAYS_SKELETON_ROWS = Array.from({ length: 1 }, (_, index) => ({
  id: `top-plays-skeleton-row-${index}`,
  cardCount: 8,
  titleWidth: 62,
}));

const DESKTOP_BREAKPOINT = 992;
const LARGE_DESKTOP_BREAKPOINT = 1200;
const SKELETON_CARD_WIDTH = 201;
const SKELETON_CARD_HEIGHT = 281;

const getDefaultCardCount = (variant) =>
  variant === "cards" ? DEFAULT_CARD_COUNTS.cards : DEFAULT_CARD_COUNTS.app;

const getResponsiveCardCount = (variant) => {
  if (typeof window === "undefined") {
    return getDefaultCardCount(variant);
  }

  const viewportWidth = window.innerWidth;
  if (viewportWidth < DESKTOP_BREAKPOINT) {
    return getDefaultCardCount(variant);
  }

  const viewportHeight = window.innerHeight;
  const sidebarWidth = viewportWidth >= LARGE_DESKTOP_BREAKPOINT ? 260 : 220;
  const horizontalChrome = viewportWidth >= DESKTOP_BREAKPOINT ? 48 : 32;
  const verticalChrome =
    variant === "app"
      ? viewportWidth >= DESKTOP_BREAKPOINT
        ? 360
        : 320
      : 140;
  const availableWidth = Math.max(
    viewportWidth - sidebarWidth - horizontalChrome,
    SKELETON_CARD_WIDTH
  );
  const availableHeight = Math.max(
    viewportHeight - verticalChrome,
    SKELETON_CARD_HEIGHT
  );
  const columns = Math.max(1, Math.floor(availableWidth / SKELETON_CARD_WIDTH));
  const rows = Math.max(2, Math.ceil(availableHeight / SKELETON_CARD_HEIGHT));
  const minimumDesktopCards = viewportWidth >= LARGE_DESKTOP_BREAKPOINT ? 15 : 10;

  return Math.max(columns * rows, minimumDesktopCards);
};

const SkeletonBlock = ({ className = "", style }) => (
  <span
    aria-hidden="true"
    className={`athlete-board-skeleton__block ${className}`.trim()}
    style={style}
  />
);

const SkeletonStickyAccountIcon = () => (
  <svg className="sticky-menu__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M18.685 19.097A9.723 9.723 0 0 0 21.75 12c0-5.385-4.365-9.75-9.75-9.75S2.25 6.615 2.25 12a9.723 9.723 0 0 0 3.065 7.097A9.716 9.716 0 0 0 12 21.75a9.716 9.716 0 0 0 6.685-2.653Zm-12.54-1.285A7.486 7.486 0 0 1 12 15a7.486 7.486 0 0 1 5.855 2.812A8.224 8.224 0 0 1 12 20.25a8.224 8.224 0 0 1-5.855-2.438ZM15.75 9a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z"
      clipRule="evenodd"
    />
  </svg>
);

const SkeletonStickyLineupsIcon = () => (
  <svg className="sticky-menu__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M2.625 6.75a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Zm4.875 0A.75.75 0 0 1 8.25 6h12a.75.75 0 0 1 0 1.5h-12a.75.75 0 0 1-.75-.75ZM2.625 12a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0ZM7.5 12a.75.75 0 0 1 .75-.75h12a.75.75 0 0 1 0 1.5h-12A.75.75 0 0 1 7.5 12Zm-4.875 5.25a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Zm4.875 0a.75.75 0 0 1 .75-.75h12a.75.75 0 0 1 0 1.5h-12a.75.75 0 0 1-.75-.75Z"
      clipRule="evenodd"
    />
  </svg>
);

const SkeletonStickyPromoIcon = () => (
  <svg className="sticky-menu__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M9.375 3a1.875 1.875 0 0 0 0 3.75h1.875v4.5H3.375A1.875 1.875 0 0 1 1.5 9.375v-.75c0-1.036.84-1.875 1.875-1.875h3.193A3.375 3.375 0 0 1 12 2.753a3.375 3.375 0 0 1 5.432 3.997h3.943c1.035 0 1.875.84 1.875 1.875v.75c0 1.036-.84 1.875-1.875 1.875H12.75v-4.5h1.875a1.875 1.875 0 1 0-1.875-1.875V6.75h-1.5V4.875C11.25 3.839 10.41 3 9.375 3ZM11.25 12.75H3v6.75a2.25 2.25 0 0 0 2.25 2.25h6v-9ZM12.75 12.75v9h6.75a2.25 2.25 0 0 0 2.25-2.25v-6.75h-9Z" />
  </svg>
);

const SkeletonStickySupportIcon = () => (
  <svg className="sticky-menu__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M4.804 21.644A6.707 6.707 0 0 0 6 21.75a6.721 6.721 0 0 0 3.583-1.029c.774.182 1.584.279 2.417.279 5.322 0 9.75-3.97 9.75-9 0-5.03-4.428-9-9.75-9s-9.75 3.97-9.75 9c0 2.409 1.025 4.587 2.674 6.192.232.226.277.428.254.543a3.73 3.73 0 0 1-.814 1.686.75.75 0 0 0 .44 1.223ZM8.25 10.875a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25ZM10.875 12a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Zm4.875-1.125a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25Z"
      clipRule="evenodd"
    />
  </svg>
);

const SkeletonStickyMenuItem = ({ children, href, label, loginRequired = false, onClick }) => {
  const content = (
    <>
      <span className="sticky-menu__icon-wrap">{children}</span>
      <span className="sticky-menu__label">{label}</span>
    </>
  );

  if (href) {
    return (
      <a className="sticky-menu__item" href={href} onClick={onClick}>
        {content}
      </a>
    );
  }

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      event.currentTarget.click();
    }
  };

  return (
    <div
      className="sticky-menu__item"
      data-ww-login-trigger={loginRequired ? "true" : undefined}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
    >
      {content}
    </div>
  );
};

export const MobileStickyMenuShell = ({ loggedIn = false }) => (
  <div
    className="sticky-menu d-md-none athlete-board-skeleton__sticky-menu"
    data-ww-sticky-chrome="menu"
  >
    <SkeletonStickyMenuItem href={loggedIn ? "/my-account/" : undefined} label="Account" loginRequired={!loggedIn}>
      <SkeletonStickyAccountIcon />
    </SkeletonStickyMenuItem>
    <SkeletonStickyMenuItem href={loggedIn ? "/my-lineup/" : undefined} label="Lineups" loginRequired={!loggedIn}>
      <SkeletonStickyLineupsIcon />
    </SkeletonStickyMenuItem>
    <SkeletonStickyMenuItem href="/promos/" label="Promos">
      <SkeletonStickyPromoIcon />
    </SkeletonStickyMenuItem>
    <SkeletonStickyMenuItem href="#ww-chat" label="Support" onClick={handleSkeletonSupportClick}>
      <SkeletonStickySupportIcon />
    </SkeletonStickyMenuItem>
  </div>
);

const renderSkeletonSportIcon = (sport) => {
  if (String(sport || "").toLowerCase() === "topplays") {
    return <i aria-hidden="true" className="fa-solid fa-trophy ww-inline-icon" />;
  }

  return <SportIcon aria-hidden="true" className="ww-inline-icon mr-1" sport={sport} />;
};

const SportButton = ({ sport, label, selected = false }) => {
  const isTopPlaysButton = String(sport || "").toLowerCase() === "topplays";

  return (
    <button
      aria-pressed={selected}
      data-prop="sport"
      className={[
        "sport-btn",
        "select-btn",
        selected ? "selected" : "",
        isTopPlaysButton ? "sport-btn--gradient-stroke" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={() => handleSkeletonSportSelection(sport)}
      type="button"
      value={sport}
    >
      {renderSkeletonSportIcon(sport)}{label}
    </button>
  );
};

const StatButton = ({ label, selected = false, Icon }) => (
  <button
    aria-hidden="true"
    className={[
      "select-btn",
      selected ? "selected" : "",
    ]
      .filter(Boolean)
      .join(" ")}
    data-prop="stat"
    style={{ display: "inline-block", marginRight: 12 }}
    tabIndex={-1}
    type="button"
    value={label.toLowerCase()}
  >
    {Icon ? <Icon aria-hidden="true" className="ww-inline-icon mr-1" /> : null}
    {label}
  </button>
);

const GameCard = ({ selected = false }) => (
  <div
    className={[
      "ww-games-slider__slide",
      "athlete-board-skeleton__game-slide",
    ]
      .filter(Boolean)
      .join(" ")}
  >
    <button
      aria-hidden="true"
      className={`ww-games-card athlete-board-skeleton__game-card${selected ? " is-selected" : ""}`}
      tabIndex={-1}
      type="button"
    >
      <div className="ww-games-card__body">
        <div className="ww-games-card__team">
          <SkeletonBlock className="athlete-board-skeleton__game-logo-block" />
        </div>
        <div className="ww-games-card__team ww-games-card__team--no-logo">
        </div>
      </div>
      <div className="ww-games-card__footer">
      </div>
    </button>
  </div>
);

const UtilityLink = ({ label, Icon, className, linkHref, onClick }) => {
  const classes = ["athlete-board-skeleton__utility-link", className].filter(Boolean).join(" ");

  if (linkHref) {
    return (
      <span className={classes}>
        <a href={linkHref} onClick={onClick} tabIndex={-1}>
          {Icon === ThemeModeIcon ? <ThemeModeIcon aria-hidden="true" className="ww-inline-icon mr-1" darkMode /> : Icon ? <Icon aria-hidden="true" className="ww-inline-icon mr-1" /> : null} {label}
        </a>
      </span>
    );
  }

  return (
    <span className={classes}>
      {Icon === ThemeModeIcon ? <ThemeModeIcon aria-hidden="true" className="ww-inline-icon mr-1" darkMode /> : Icon ? <Icon aria-hidden="true" className="ww-inline-icon mr-1" /> : null} {label}
    </span>
  );
};

const SearchShell = () => (
  <div className="col-md-5 col-sm-12 mb-3 pl-0 pr-3 board-search-shell">
    <label htmlFor="search-input" className="sr-only">
      Search Athletes
    </label>
    <span className="board-search">
      <MagnifyingGlassIcon className="board-search-icon" aria-hidden="true" />
      <input
        aria-hidden="true"
        autoComplete="off"
        className="board-search-input"
        defaultValue=""
        id="search-input"
        placeholder="Search"
        tabIndex={-1}
        type="text"
      />
    </span>
  </div>
);

const renderCards = (count = 5, statLabel, previewRows = []) =>
  Array.from({ length: count }, (_, index) => (
    <div
      aria-hidden="true"
      className={`athlete-card flex-fill athlete-board-skeleton__card${previewRows[index] ? " athlete-board-skeleton__card--preview" : ""}${usesCompactHeadshot(previewRows[index]?.league) ? " athlete-card--compact-headshot" : ""}${previewRows[index] && isFallbackHeadshotImage(previewRows[index].image) ? " athlete-card--fallback-headshot" : ""}`}
      key={`athlete-board-skeleton-card-${index}`}
    >
      <div className="outer-mf">
        <div className="more-info">
          <button aria-hidden="true" className="graph" tabIndex={-1} type="button">
            <ChartBarIcon aria-hidden="true" className="ww-card-icon" />
          </button>
        </div>
      </div>

      <div className={`athlete-headshot${previewRows[index] ? " athlete-headshot--cached-preview" : ""}`}>
        {previewRows[index] && (
          <AthleteHeadshotImage src={previewRows[index].image} faceNormalization={previewRows[index].image_face} alt="" priority={index < 6} />
        )}
      </div>

      <div className="athlete-meta-pill athlete-board-skeleton__meta-pill" />

      <h3 className="name">{previewRows[index]?.name || ""}</h3>

      <div className="card-txt" />

      <div className="avg-stat mt-1 mb-2 athlete-board-skeleton__avg-stat">
        {previewRows[index] ? (
          <span className="athlete-board-skeleton__line-loading">
            <span className="athlete-board-skeleton__line-spinner" />
          </span>
        ) : null}
      </div>

      <div className="row ml-btns">
        <div className="col-6 border-middle ml-btn athlete-board-skeleton__card-action">
          <button aria-hidden="true" className="athlete-btn" tabIndex={-1} type="button">
            <span>
              <SelectionDirectionIcon className="ww-inline-icon mr-1" direction="more" />
              More
            </span>
          </button>
        </div>
        <div className="col-6 ml-btn athlete-board-skeleton__card-action">
          <button aria-hidden="true" className="athlete-btn" tabIndex={-1} type="button">
            <span>
              <SelectionDirectionIcon className="ww-inline-icon mr-1" direction="less" />
              Less
            </span>
          </button>
        </div>
      </div>
    </div>
  ));

export const CollapsedSidebarShell = () => (
  <div className="align-top toggle-sidebar athlete-board-skeleton__sidebar-collapsed" aria-hidden="true">
    <span>Expand</span>
    <ArrowRightIcon aria-hidden="true" className="ww-inline-icon" />
  </div>
);

export const PromoOffersShell = () => (
  <div className="promo-offers-shell mt-2 athlete-board-skeleton__promo-shell" aria-hidden="true">
    <div className="promo-offers athlete-board-skeleton__promo-grid">
      {[0, 1, 2, 3].map((index) => (
        <div
          className={`promo-offer-card athlete-board-skeleton__promo-card${index === 0 ? " is-featured" : ""}`}
          key={`promo-skeleton-${index}`}
        >
        </div>
      ))}
    </div>
  </div>
);

const SportsSelectionShell = ({ keyPrefix = "sport-skeleton", sports = [], selectedSport = "" }) => {
  const sliderRef = React.useRef(null);
  const sportItems = getSkeletonSportItems(sports, selectedSport);
  const sportsKey = sportItems.map((item) => item.sport).join("|");

  React.useEffect(() => {
    let animationFrame = null;

    const centerSelectedSport = () => {
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
      }

      animationFrame = window.requestAnimationFrame(() => {
        animationFrame = null;
        const slider = sliderRef.current;
        const selectedButton = slider?.querySelector(".sport-btn.selected");
        if (!slider || !selectedButton) {
          return;
        }

        const sliderRect = slider.getBoundingClientRect();
        const selectedRect = selectedButton.getBoundingClientRect();
        const targetLeft = slider.scrollLeft
          + selectedRect.left
          - sliderRect.left
          - ((sliderRect.width - selectedRect.width) / 2);
        const maxLeft = Math.max(0, slider.scrollWidth - slider.clientWidth);
        slider.scrollLeft = Math.max(0, Math.min(targetLeft, maxLeft));
      });
    };

    centerSelectedSport();
    window.addEventListener("resize", centerSelectedSport);
    window.addEventListener("ww:median-app-resumed", centerSelectedSport);

    return () => {
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
      }
      window.removeEventListener("resize", centerSelectedSport);
      window.removeEventListener("ww:median-app-resumed", centerSelectedSport);
    };
  }, [selectedSport, sportsKey]);

  return (
    <div className="ww-board-section ww-board-section--sports mb-3 athlete-board-skeleton__selection-row athlete-board-skeleton__selection-row--sports">
      <div className="sport-selection-slider athlete-board-skeleton__sports-slider" ref={sliderRef}>
        {sportItems.map((item, index) => (
          <SportButton
            key={`${keyPrefix}-${index}`}
            label={item.label}
            selected={item.selected}
            sport={item.sport}
          />
        ))}
      </div>
    </div>
  );
};

const StatsSelectionShell = () => (
  <div
    className="ww-board-section ww-board-section--stats hide-scrollbar athlete-board-skeleton__selection-row athlete-board-skeleton__selection-row--stats"
    style={STAT_ROW_STYLE}
  >
    {TOP_PLAYS_STAT_ITEMS.map((item, index) => (
      <StatButton
        key={`top-plays-stat-skeleton-${index}`}
        Icon={item.Icon}
        label={item.label}
        selected={item.selected}
      />
    ))}
  </div>
);

const EventsShell = ({ keyPrefix = "game-skeleton" }) => (
  <div className="ww-board-section ww-board-section--events">
    <div className="ww-board-section-label">Events</div>
    <div className="athlete-board-skeleton__games-row ww-games-slider-shell is-loading">
      <div className="ww-games-slider athlete-board-skeleton__games-slider">
        {GAME_ITEMS.map((item, index) => (
          <GameCard key={`${keyPrefix}-${index}`} selected={item.selected} />
        ))}
      </div>
    </div>
  </div>
);

const AppShell = ({ cardCount, statLabel, sports, selectedSport }) => (
  <div className="text-center mt-1">
    <SportsSelectionShell sports={sports} selectedSport={selectedSport} />
    <StatsSelectionShell />
    <EventsShell />

    <div className="ww-board-section ww-board-section--utility d-flex flex-wrap mt-2 mt-md-4">
      <div className="col-md-7 col-sm-12 p-010 d-flex align-items-center">
        <div className="how-to-play">
          <span className="mr-2">
            <MagnifyingGlassIcon aria-hidden="true" className="ww-inline-icon mr-1" /> Search
          </span>{" "}
          <span className="ml-2 mr-2">
            <PlayCircleIcon aria-hidden="true" className="ww-inline-icon mr-1" /> Rules
          </span>{" "}
          <span className="ml-2 mr-2">
            <InformationCircleIcon aria-hidden="true" className="ww-inline-icon mr-1" /> Icons
          </span>{" "}
          <span className="ml-2 mr-2 d-none d-md-inline-block">
            <a href="#" onClick={handleSkeletonSupportClick} tabIndex={-1}>
              <InformationCircleIcon aria-hidden="true" className="ww-inline-icon mr-1" /> Support
            </a>
          </span>{" "}
          <span className="ml-2">
            <ThemeModeIcon aria-hidden="true" className="ww-inline-icon mr-1" darkMode /> Light Mode
          </span>
        </div>
      </div>

      <SearchShell />
    </div>

    <div className="ww-board-section ww-board-section--cards d-flex flex-wrap flex-md-nowrap athlete-board-skeleton__board-layout">
      <div className="d-flex flex-wrap justify-content-md-start align-self-baseline h-100 athlete-card-grid athlete-card-grid--multiple athlete-board-skeleton__cards pr-3">
        {renderCards(cardCount, statLabel)}
      </div>
      <CollapsedSidebarShell />
    </div>
  </div>
);

const CardsShell = ({ cardCount, statLabel, previewRows }) => (
  <div className="d-flex flex-wrap justify-content-md-start align-self-baseline h-100 athlete-card-grid athlete-card-grid--multiple athlete-board-skeleton__cards">
    {renderCards(cardCount, statLabel, previewRows)}
  </div>
);

const TopPlaysRowsShell = ({ statLabel }) => (
  <div className="top-plays-board">
    {TOP_PLAYS_SKELETON_ROWS.map((row) => (
      <section className="top-plays-row athlete-board-skeleton__top-plays-row" key={row.id} aria-hidden="true">
        <div className="top-plays-row__header">
          <h2 className="top-plays-row__title">
            <SkeletonBlock
              className="athlete-board-skeleton__top-plays-title-block"
              style={{ width: `${row.titleWidth}px` }}
            />
          </h2>
          <div className="top-plays-row__controls">
            <button
              aria-hidden="true"
              className="top-plays-row__control top-plays-row__control--prev"
              tabIndex={-1}
              type="button"
            >
              <ArrowRightIcon aria-hidden="true" className="ww-inline-icon" />
            </button>
            <button
              aria-hidden="true"
              className="top-plays-row__control top-plays-row__control--next"
              tabIndex={-1}
              type="button"
            >
              <ArrowRightIcon aria-hidden="true" className="ww-inline-icon" />
            </button>
          </div>
        </div>
        <div className="top-plays-row__scroller">
          {renderCards(row.cardCount, statLabel)}
        </div>
      </section>
    ))}
  </div>
);

const TopPlaysFullShell = ({ statLabel, sports, selectedSport }) => (
  <div className="text-center mt-1">
    <SportsSelectionShell
      keyPrefix="top-plays-sport-skeleton"
      sports={sports}
      selectedSport={selectedSport}
    />
    <EventsShell keyPrefix="top-plays-game-skeleton" />

    <div className="ww-board-section ww-board-section--promos">
      <PromoOffersShell />
    </div>

    <div className="ww-board-section ww-board-section--utility d-flex flex-wrap mt-2 mt-md-4">
      <div className="col-md-7 col-sm-12 p-010 d-flex align-items-center">
        <div className="how-to-play">
          <span className="mr-2">
            <MagnifyingGlassIcon aria-hidden="true" className="ww-inline-icon mr-1" /> Search
          </span>{" "}
          <span className="ml-2 mr-2">
            <PlayCircleIcon aria-hidden="true" className="ww-inline-icon mr-1" /> Rules
          </span>{" "}
          <span className="ml-2 mr-2">
            <InformationCircleIcon aria-hidden="true" className="ww-inline-icon mr-1" /> Icons
          </span>{" "}
          <span className="ml-2 mr-2 d-none d-md-inline-block">
            <a href="#" onClick={handleSkeletonSupportClick} tabIndex={-1}>
              <InformationCircleIcon aria-hidden="true" className="ww-inline-icon mr-1" /> Support
            </a>
          </span>{" "}
          <span className="ml-2">
            <ThemeModeIcon aria-hidden="true" className="ww-inline-icon mr-1" darkMode /> Light Mode
          </span>
        </div>
      </div>

      <SearchShell />
    </div>

    <div className="ww-board-section ww-board-section--cards d-flex flex-wrap flex-md-nowrap athlete-board-skeleton__board-layout">
      <div className="top-plays-board-shell">
        <TopPlaysRowsShell statLabel={statLabel} />
      </div>
      <CollapsedSidebarShell />
    </div>
  </div>
);

export default function AthleteBoardSkeleton({
  variant = "app",
  cardCount,
  statLabel = "Stat",
  stickyMenu = null,
  sports = [],
  selectedSport = "",
  previewRows = [],
}) {
  const cardsOnly = variant === "cards";
  const statsOnly = variant === "stats";
  const topPlaysFull = variant === "topplays";
  const topPlaysCardsOnly = variant === "topplays-cards";
  const [responsiveCardCount, setResponsiveCardCount] = React.useState(() =>
    getResponsiveCardCount(variant)
  );

  React.useEffect(() => {
    if (typeof cardCount === "number" || typeof window === "undefined") {
      return undefined;
    }

    const updateResponsiveCardCount = () => {
      setResponsiveCardCount(getResponsiveCardCount(variant));
    };

    updateResponsiveCardCount();
    window.addEventListener("resize", updateResponsiveCardCount);

    return () => {
      window.removeEventListener("resize", updateResponsiveCardCount);
    };
  }, [cardCount, variant]);

  const resolvedCardCount =
    typeof cardCount === "number" ? cardCount : responsiveCardCount;

  return (
    <>
      <div
        aria-busy="true"
        aria-live="polite"
        className={`athlete-board-skeleton athlete-board-skeleton--${variant}`}
        role="status"
      >
        <span className="sr-only">
          {cardsOnly || topPlaysCardsOnly
            ? "Loading athletes"
            : statsOnly
              ? "Loading stats"
              : "Loading athlete board"}
        </span>
        {statsOnly ? (
          <StatsSelectionShell />
        ) : topPlaysFull ? (
          <TopPlaysFullShell statLabel={statLabel} sports={sports} selectedSport={selectedSport} />
        ) : topPlaysCardsOnly ? (
          <TopPlaysRowsShell statLabel={statLabel} />
        ) : cardsOnly ? (
          <CardsShell cardCount={resolvedCardCount} statLabel={statLabel} previewRows={previewRows} />
        ) : (
          <AppShell
            cardCount={resolvedCardCount}
            statLabel={statLabel}
            sports={sports}
            selectedSport={selectedSport}
          />
        )}
      </div>
      {!cardsOnly && !topPlaysCardsOnly && !statsOnly ? stickyMenu : null}
    </>
  );
}
